import { api, apiRequest } from '@/core/http/apiClient';
import { download } from '@/core/http/files';
import { exportFile } from '@/core/http/helpers';
import type {
  DashboardSubmissionSummary,
  DashboardSummary,
  DashboardTileList,
  DashboardTileListQuery,
  NewSupportTicket,
  SupportTicketCreated,
  SupportTicketOptions,
  SupportTicketQuery,
  SupportTicketReplyCreated,
  SupportTicketResponse,
  SupportTicketRow,
  SupportTicketThread,
  UserLogExportQuery,
  UserLogOptions,
  UserLogQuery,
  UserLogResponse,
  WorkspaceNotifications,
} from '@/core/models/workspace.models';

/** Header notifications, dashboard figures, the user log and support tickets (`api/v1/Workspace/`). */

export { WorkspaceNotificationType } from '@/core/models/workspace.models';
export type { WorkspaceNotification, WorkspaceNotifications } from '@/core/models/workspace.models';

const base = 'Workspace/';

export const workspaceService = {
  getDashboard: () => api.get<DashboardSummary>(`${base}getDashboard`),

  /** The Accounts Submission Summary and the monthly submitted chart for a year (default: this year). */
  getSubmissionSummary: (year?: number) =>
    api.get<DashboardSubmissionSummary>(
      `${base}getSubmissionSummary`,
      year === undefined ? undefined : { params: { year } },
    ),

  getNotifications: () => api.get<WorkspaceNotifications>(`${base}getNotifications`),

  /** Marks notification keys read (or unread) for the signed-in user and returns the refreshed list. */
  markNotifications: (keys: string[], isRead = true) =>
    api.post<WorkspaceNotifications>(`${base}markNotifications`, { keys, isRead }),

  /** Removes notification keys from this user's list (or puts them back) and returns the refreshed list. */
  dismissNotifications: (keys: string[], isDismissed = true) =>
    api.post<WorkspaceNotifications>(`${base}dismissNotifications`, { keys, isDismissed }),

  /** The rows behind one dashboard tile. */
  getDashboardTileList: (query: DashboardTileListQuery) =>
    api.post<DashboardTileList>(`${base}getDashboardTileList`, query),

  getUserLogs: (query: UserLogQuery) => api.post<UserLogResponse>(`${base}getUserLogs`, query),

  getUserLogOptions: () => api.get<UserLogOptions>(`${base}getUserLogOptions`),

  exportUserLogs: (query: UserLogExportQuery) => exportFile(`${base}exportUserLogs`, query, !!query.isPrint),

  getSupportTickets: (query: SupportTicketQuery) => api.post<SupportTicketResponse>(`${base}getSupportTickets`, query),

  getSupportTicketOptions: () => api.get<SupportTicketOptions>(`${base}getSupportTicketOptions`),

  /** 0 Open, 1 Pending, 2 Resolved, 3 Closed. */
  setSupportTicketStatus: (id: string, status: number) =>
    api.post<SupportTicketRow>(`${base}setSupportTicketStatus`, { id, status }),

  /** 1 Low, 2 Normal, 3 High, 4 Urgent. */
  setSupportTicketPriority: (id: string, priority: number) =>
    api.post<SupportTicketRow>(`${base}setSupportTicketPriority`, { id, priority }),

  /** Multipart upload: no Content-Type header, so the browser sets the boundary. */
  saveSupportTicket: (ticket: NewSupportTicket) => {
    const form = new FormData();
    const fields: Array<[string, string | null | undefined]> = [
      ['phone', ticket.phone],
      ['company', ticket.company],
      ['helpTopic', ticket.helpTopic],
      ['module', ticket.module],
      ['subject', ticket.subject],
      ['message', ticket.message],
    ];
    fields.forEach(([name, value]) => {
      if (value !== null && value !== undefined && `${value}`.trim() !== '') {
        form.append(name, `${value}`);
      }
    });
    ticket.files.forEach((file) => form.append('files', file, file.name));

    return api.post<SupportTicketCreated>(`${base}saveSupportTicket`, form);
  },

  /** APT239: one ticket with its conversation (first message, then every reply oldest first). */
  getSupportTicket: (id: string) => api.get<SupportTicketThread>(`${base}getSupportTicket/${id}`),

  /** APT239: saves an attachment on a ticket's thread, named by the API's Content-Disposition. */
  downloadSupportTicketAttachment: (attachmentId: string) =>
    apiRequest<Blob>('GET', `${base}getSupportTicketAttachment/${attachmentId}`, { responseType: 'blob' }).then(
      download,
    ),

  /** APT239: a reply on a ticket - multipart, so the browser sets the boundary. */
  saveSupportTicketReply: (id: string, message: string, files: File[]) => {
    const form = new FormData();
    form.append('id', id);
    form.append('message', message);
    files.forEach((file) => form.append('files', file, file.name));

    return api.post<SupportTicketReplyCreated>(`${base}saveSupportTicketReply`, form);
  },
};
